import { memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Ban } from "lucide-react";
import { formatCurrency } from "@/shared/lib/formatters";
import type { InvoiceResponseDTO } from "../types/invoice.types";

interface InvoiceCancelDialogProps {
  open: boolean;
  loading: boolean;
  invoice: InvoiceResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Dialog de confirmação de solicitação de cancelamento de uma nota.
 *
 * O cancelamento é assíncrono — o backend processa via FocusNFe e
 * atualiza o status posteriormente. Disponível apenas para notas com
 * `status === 'ISSUED'` (validado pelo `InvoicesTable`).
 *
 * @example
 * <InvoiceCancelDialog
 *   open={dialogMode === 'cancel'}
 *   loading={dialogLoading}
 *   invoice={selectedInvoice}
 *   onClose={closeDialog}
 *   onConfirm={handleCancelConfirm}
 * />
 */
export const InvoiceCancelDialog = memo(function InvoiceCancelDialog({
  open,
  loading,
  invoice,
  onClose,
  onConfirm,
}: InvoiceCancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancelar Nota Fiscal</DialogTitle>
          <DialogDescription>
            Solicitar cancelamento desta nota fiscal junto ao FocusNFe?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-amber-500/10 rounded-md border border-amber-500/20">
            <div className="flex items-start gap-3">
              <Ban className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Nota Fiscal #{invoice?.id}</p>
                {invoice?.externalId ? (
                  <p className="text-sm text-muted-foreground">
                    ID Externo: {invoice.externalId}
                  </p>
                ) : null}
                {invoice ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(invoice.value)}
                  </p>
                ) : null}
                <p className="text-sm text-muted-foreground mt-2">
                  O cancelamento é processado de forma assíncrona. O status será
                  atualizado quando o FocusNFe responder.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Cancelando..." : "Cancelar Nota"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
